import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TocRoundedIcon from '@mui/icons-material/TocRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { addMinutes, addMonths, isBefore, set, startOfMonth, subMonths } from 'date-fns';
import PageHeader from '@features/PageHeader';
import PageContainer from '@components/Layout/PageContainer';
import IntegrationRequired from '@features/IntegrationRequired';
import Button from '@components/Button';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { ModalConfirm } from '@components/Modal';
import { SocialPostsIcon } from 'assets/images/icons';
import { useTranslation, useLocations } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { socialPostsActions, alertActions } from '@actions';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { FILTERABLE_CAMPAIGN_STATUSES } from '@constants/social-posts';
import { socialPostsService } from '@services';
import { ModalType } from '@constants/modal';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { handleApiError } from '@helpers/api';
import SocialPostsTable from './SocialPostsTable';
import SocialPostsCalendar from './SocialPostsCalendar';
import EditModal from './EditModal';
import FiltersPanel from './FiltersPanel';

const ALL_STATUSES = FILTERABLE_CAMPAIGN_STATUSES;

const VIEW_MODE = { TABLE: 'table', CALENDAR: 'calendar' };
const VIEW_MODE_STORAGE_KEY = 'social-posts-view-mode';

const getInitialViewMode = () => {
    try {
        const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        return stored === VIEW_MODE.TABLE || stored === VIEW_MODE.CALENDAR ? stored : VIEW_MODE.CALENDAR;
    } catch {
        return VIEW_MODE.CALENDAR;
    }
};

const nextHalfHourSlot = date => {
    const base = set(date, { seconds: 0, milliseconds: 0 });
    const minutes = base.getMinutes();
    return addMinutes(base, minutes < 30 ? 30 - minutes : 60 - minutes);
};

const ViewToggle = styled(RadioButtonsGroup)({
    width: 220,
    display: 'block',
    '& .MuiFormControlLabel-root': {
        height: 32,
        padding: '0 4px',
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
});

const ToggleLabel = styled('span')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    lineHeight: 1,
    '&& svg': { width: 18, height: 18, fontSize: 18, display: 'block' },
});

const PostsPage = () => {
    const { t } = useTranslation();
    const { hasFullAccess } = usePermissions();
    const [pageRequests, setPageRequests] = useState(0);
    const [rowsPerPageRequests, setRowsPerPageRequests] = useState(DEFAULT_PAGE_SIZE);
    const [selectedSocialPost, setSelectedSocialPost] = useState({});
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [modal, setModal] = useState({ open: false, type: ModalType.Create });
    const [viewMode, setViewMode] = useState(getInitialViewMode);
    const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
    const [initialScheduledTime, setInitialScheduledTime] = useState(null);

    const account = useSelector(state => state.account?.account);
    const { data: socialPostsData } = useSelector(state => state.socialPosts);
    const { mapLocationsToFormValue } = useLocations();

    const INTEGRATION_CONNECTED =
        isIntegrationConnected(account, AccIntegrationName.Google) ||
        isIntegrationConnected(account, AccIntegrationName.Facebook);

    const availableCompanies = useMemo(
        () =>
            (account?.companies || []).filter(c =>
                c?.profiles?.some(p => p.type === AccIntegrationName.Google || p.type === AccIntegrationName.Facebook),
            ),
        [account?.companies],
    );

    const [availablePlatforms, setAvailablePlatforms] = useState([]);
    const [filter, setFilter] = useState({
        companies: [],
        platforms: [],
        statuses: ALL_STATUSES,
    });

    const firstRender = useRef(true);
    const platformsFetched = useRef(false);
    const dispatch = useDispatch();

    // Refetch keeps the platforms dropdown in sync after create/delete. If the user previously had
    // every available platform selected ("no narrowing"), keep that semantic — otherwise leave the
    // explicit selection alone so newly-available platforms just appear in the dropdown.
    const refetchAvailablePlatforms = useCallback(async () => {
        try {
            const newPlatforms = await socialPostsService.getAvailablePlatforms();
            const wasAllSelected = filter.platforms.length === availablePlatforms.length;
            setAvailablePlatforms(newPlatforms);
            if (wasAllSelected) {
                setFilter(prev => ({ ...prev, platforms: newPlatforms }));
            }
        } catch (error) {
            handleApiError(error);
        }
    }, [filter.platforms.length, availablePlatforms.length]);

    useEffect(() => {
        if (!INTEGRATION_CONNECTED || platformsFetched.current) return;
        platformsFetched.current = true;
        refetchAvailablePlatforms();
    }, [INTEGRATION_CONNECTED, refetchAvailablePlatforms]);

    useEffect(() => {
        if (availableCompanies.length) {
            setFilter(prev => ({ ...prev, companies: mapLocationsToFormValue(availableCompanies) }));
        }
    }, [availableCompanies]);

    const buildQuery = f => ({
        companies: f.companies.length === availableCompanies.length ? [] : f.companies.map(c => c.id),
        platforms: f.platforms.length === availablePlatforms.length ? [] : f.platforms,
        statuses: f.statuses.length === ALL_STATUSES.length ? [] : f.statuses,
    });

    const currentQuery = useMemo(() => buildQuery(filter), [filter, availableCompanies, availablePlatforms]);

    const calendarQuery = useMemo(
        () => ({
            ...currentQuery,
            scheduledTimeFrom: startOfMonth(subMonths(calendarMonth, 1)).toISOString(),
            scheduledTimeTo: startOfMonth(addMonths(calendarMonth, 2)).toISOString(),
            returnAll: true,
        }),
        [currentQuery, calendarMonth],
    );

    const refetchPosts = useCallback(
        (page = pageRequests) => {
            if (viewMode === VIEW_MODE.CALENDAR) {
                dispatch(socialPostsActions.get(0, rowsPerPageRequests, calendarQuery));
            } else {
                dispatch(socialPostsActions.get(page, rowsPerPageRequests, currentQuery));
            }
        },
        [viewMode, dispatch, rowsPerPageRequests, pageRequests, currentQuery, calendarQuery],
    );

    useEffect(() => {
        if (firstRender.current && INTEGRATION_CONNECTED) {
            refetchPosts();
            firstRender.current = false;
        }
    }, [INTEGRATION_CONNECTED, dispatch]);

    useEffect(() => {
        if (!firstRender.current) {
            refetchPosts();
        }
    }, [pageRequests, rowsPerPageRequests, viewMode, calendarMonth]);

    const onPageChange = (_, newPage) => setPageRequests(newPage);

    const onRowsPerPageChange = event => {
        setRowsPerPageRequests(parseInt(event.target.value, 10));
        setPageRequests(0);
    };

    const onApplyFilters = () => {
        setPageRequests(0);
        dispatch(socialPostsActions.reset());
        refetchPosts(0);
    };

    const onViewModeChange = nextView => {
        if (nextView === viewMode) return;
        try {
            localStorage.setItem(VIEW_MODE_STORAGE_KEY, nextView);
        } catch {
            // ignore (private mode / storage disabled)
        }
        dispatch(socialPostsActions.reset());
        setViewMode(nextView);
    };

    const onCreateOnDay = day => {
        const tenAm = set(day, { hours: 10, minutes: 0, seconds: 0, milliseconds: 0 });
        const scheduled = isBefore(tenAm, new Date()) ? nextHalfHourSlot(new Date()) : tenAm;
        setInitialScheduledTime(scheduled);
        setSelectedSocialPost({});
        setModal({ open: true, type: ModalType.Create });
    };

    const onReschedule = useCallback(
        async (campaign, newDate) => {
            const newScheduledTime = newDate.toISOString();
            dispatch(
                socialPostsActions.setSocialPosts(
                    socialPostsData.map(c => (c.id === campaign.id ? { ...c, scheduledTime: newScheduledTime } : c)),
                ),
            );
            try {
                await socialPostsService.rescheduleSocialPost(campaign.id, newScheduledTime);
                dispatch(alertActions.success(t('SocialPosts.rescheduleSuccess')));
            } catch (error) {
                handleApiError(error, 'SocialPosts.rescheduleFail');
            } finally {
                refetchPosts();
            }
        },
        [socialPostsData, dispatch, t, refetchPosts],
    );

    const onClickSocialPost = (post, type) => {
        setSelectedSocialPost(post);
        switch (type) {
            case 'delete':
                setDeleteModalOpen(true);
                break;
            case 'edit':
                setModal({ open: true, type: ModalType.Edit });
                break;
            case 'view':
                setModal({ open: true, type: ModalType.View });
                break;
            default:
                break;
        }
    };

    const onDeleteSocialPost = async () => {
        try {
            await socialPostsService.deleteSocialPost(selectedSocialPost.id);
            refetchPosts();
            refetchAvailablePlatforms();
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setModal({ open: false, type: ModalType.Create });
            setSelectedSocialPost({});
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail');
        }
        setDeleteModalOpen(false);
    };

    return (
        <PageContainer>
            {!INTEGRATION_CONNECTED ? (
                <IntegrationRequired />
            ) : (
                <>
                    <PageHeader
                        icon={<SocialPostsIcon />}
                        title={t('SocialPosts.title')}
                        subtitle={t('SocialPosts.subtitle')}
                        styles={{ marginBottom: '16px' }}
                        metrics={
                            <ViewToggle
                                name="posts-view-mode"
                                value={viewMode}
                                onChange={onViewModeChange}
                                options={[
                                    {
                                        value: VIEW_MODE.CALENDAR,
                                        label: (
                                            <ToggleLabel>
                                                <CalendarMonthRoundedIcon />
                                                {t('SocialPosts.calendarView')}
                                            </ToggleLabel>
                                        ),
                                    },
                                    {
                                        value: VIEW_MODE.TABLE,
                                        label: (
                                            <ToggleLabel>
                                                <TocRoundedIcon />
                                                {t('SocialPosts.tableView')}
                                            </ToggleLabel>
                                        ),
                                    },
                                ]}
                            />
                        }
                    >
                        {hasFullAccess('posts') && (
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setInitialScheduledTime(null);
                                    setSelectedSocialPost({});
                                    setModal({ open: true, type: ModalType.Create });
                                }}
                            >
                                {t('buttons.createNewPost')}
                            </Button>
                        )}
                        <FiltersPanel
                            filter={filter}
                            setFilter={setFilter}
                            onApplyFilters={onApplyFilters}
                            availablePlatforms={availablePlatforms}
                        />
                    </PageHeader>
                    {viewMode === VIEW_MODE.CALENDAR ? (
                        <SocialPostsCalendar
                            month={calendarMonth}
                            onMonthChange={setCalendarMonth}
                            onCreateOnDay={hasFullAccess('posts') ? onCreateOnDay : undefined}
                            onClickSocialPost={onClickSocialPost}
                            onReschedule={onReschedule}
                        />
                    ) : (
                        <SocialPostsTable
                            rowsPerPageRequests={rowsPerPageRequests}
                            pageRequests={pageRequests}
                            onPageChange={onPageChange}
                            onRowsPerPageChange={onRowsPerPageChange}
                            onClickSocialPost={onClickSocialPost}
                            currentQuery={currentQuery}
                            refetchAvailablePlatforms={refetchAvailablePlatforms}
                        />
                    )}
                </>
            )}
            {modal.open ? (
                <EditModal
                    modal={modal}
                    setModal={setModal}
                    selectedSocialPost={selectedSocialPost}
                    setSelectedSocialPost={setSelectedSocialPost}
                    refetchAvailablePlatforms={refetchAvailablePlatforms}
                    initialScheduledTime={initialScheduledTime}
                    refetchPosts={refetchPosts}
                    onDelete={() => setDeleteModalOpen(true)}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDeleteSocialPost}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </PageContainer>
    );
};

export default PostsPage;
